import { z } from 'zod';

/**
 * The website is published in English (default, at /) and Arabic (at /ar, right-to-left).
 * English is the source language stored in each content column; Arabic versions live in a
 * `translations` JSON column ({ ar: { field: text } }). Empty Arabic fields fall back to English.
 */
export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';
export const RTL_LOCALES: readonly Locale[] = ['ar'];

export const isLocale = (v: unknown): v is Locale => LOCALES.includes(v as Locale);

export type Translations = Partial<Record<Exclude<Locale, 'en'>, Record<string, string>>>;

/** Text fields that have an Arabic version, per content type, with their maximum length. */
export const TRANSLATABLE_FIELDS = {
  project: {
    title: 160,
    description: 20000,
    headline: 160,
    client: 120,
    scope: 160,
    period: 40,
    /** One per line (or comma-separated); replaces the English list */
    disciplines: 800,
  },
  projectImage: { caption: 255 },
  projectCategory: { name: 80 },
  testimonial: { clientName: 120, clientRole: 120, company: 120, quote: 3000 },
  service: { title: 120, description: 2000 },
  processStep: { title: 120, description: 2000 },
  faqItem: { question: 200, answer: 2000 },
  statistic: { label: 80 },
  clientBrand: { name: 120, storyTitle: 120, storyText: 500, storyLabel: 120 },
  siteSettings: {
    siteTitle: 120,
    metaDescription: 300,
    navAboutLabel: 40,
    navWorkLabel: 40,
    navServicesLabel: 40,
    navInsightsLabel: 40,
    navCtaLabel: 40,
    heroLightText: 200,
    heroBoldText: 120,
    heroHighlightText: 120,
    heroPrimaryLabel: 60,
    heroSecondaryLabel: 60,
    ownerName: 120,
    ownerTagline: 120,
    roles: 300,
    education: 300,
    bioPrimary: 3000,
    bioSecondary: 3000,
    location: 120,
    clientsHeading: 200,
    workHeading: 160,
    workIntro: 400,
    workNote: 200,
    workNoteDetail: 300,
    servicesHeading: 160,
    servicesIntro: 400,
    processHeading: 160,
    processIntro: 400,
    processNote: 200,
    faqHeading: 160,
    testimonialsHeading: 160,
    storyCtaHeading: 160,
    storyCtaText: 400,
    storyCtaLabel: 60,
    contactHeading: 160,
    contactIntro: 500,
    contactCtaLabel: 60,
    newsletterHeading: 160,
    newsletterIntro: 400,
    footerText: 300,
  },
} as const satisfies Record<string, Record<string, number>>;

export type TranslatableKind = keyof typeof TRANSLATABLE_FIELDS;

/**
 * Validates `{ ar: { field: text } }` for one content type: unknown fields are dropped, text is
 * trimmed and empty fields are removed (so they fall back to English).
 */
export function translationsSchema(kind: TranslatableKind) {
  const fields = TRANSLATABLE_FIELDS[kind] as Record<string, number>;
  const shape = Object.fromEntries(
    Object.entries(fields).map(([k, max]) => [
      k,
      z
        .union([z.string(), z.null(), z.undefined()])
        .transform((v) => (typeof v === 'string' ? v.trim() : ''))
        .pipe(z.string().max(max, `Maximum ${max} characters`)),
    ]),
  );
  return z.object({ ar: z.object(shape).partial().optional() }).transform((t): Translations => {
    const ar = Object.fromEntries(
      Object.entries(t.ar ?? {}).filter(([, v]) => typeof v === 'string' && v !== ''),
    ) as Record<string, string>;
    return Object.keys(ar).length ? { ar } : {};
  });
}

/** Reads a stored translations value defensively (it is user-editable JSON). */
export function asTranslations(v: unknown): Translations {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const ar = (v as Record<string, unknown>).ar;
  if (!ar || typeof ar !== 'object' || Array.isArray(ar)) return {};
  return {
    ar: Object.fromEntries(
      Object.entries(ar).filter((e): e is [string, string] => typeof e[1] === 'string' && !!e[1]),
    ),
  };
}

/** "Logo\nVisual identity" or "Logo, Visual identity" → ["Logo", "Visual identity"] */
export const splitList = (text: string) =>
  text
    .split(/\n|,|،/)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Applies the translations of `locale` to a serialized response, recursively: every object with a
 * `translations` key gets its translated fields overlaid, and the key itself is removed.
 */
export function localizeDeep<T>(value: T, locale: Locale): T {
  if (Array.isArray(value)) return value.map((v) => localizeDeep(v, locale)) as T;
  if (!value || typeof value !== 'object' || value instanceof Date) return value;
  const { translations, ...rest } = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(rest)) out[k] = localizeDeep(v, locale);
  if (locale !== 'en' && translations !== undefined) {
    const fields = asTranslations(translations)[locale] ?? {};
    for (const [k, v] of Object.entries(fields)) {
      if (!(k in out)) continue;
      out[k] = Array.isArray(out[k]) ? splitList(v) : v;
    }
  }
  return out as T;
}
